import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRowHighlight } from '../utils/useRowHighlight';
import { useAppShell } from '../contexts/AppShellContext';
import { useChats } from '../contexts/ChatsContext';
import { ADVISOR_SUPPORT_EMAILS, AdvisorSupportEmail } from '../data/advisorSupportEmails';

const JOB_ID: Record<string, string> = {
  'adv-001': 'AS-2406-001',
  'adv-002': 'AS-2406-002',
  'adv-003': 'AS-2406-003',
  'adv-004': 'AS-2406-004',
  'adv-005': 'AS-2406-005',
  'adv-006': 'AS-2406-006',
  'adv-007': 'AS-2406-007',
  'adv-008': 'AS-2406-008',
  'adv-009': 'AS-2406-009',
  'adv-010': 'AS-2406-010',
};

const PRIORITY_CLS = {
  urgent: 'bg-red-100 text-red-700',
  high:   'bg-orange-100 text-orange-700',
  normal: 'bg-gray-100 text-gray-500',
};

const STATUS_CLS: Record<string, string> = {
  'New':        'bg-blue-100 text-blue-700',
  'Processing': 'bg-orange-100 text-orange-700',
  'Responded':  'bg-green-100 text-green-700',
  'Follow-up':  'bg-purple-100 text-purple-700',
};

const COL = '120px 160px 120px 1fr 130px 80px 80px 100px';

export function AdvisorSupportPage() {
  const { setTopbar } = useAppShell();
  const { sessions } = useChats();
  const navigate = useNavigate();
  const { rowRef, highlightClass } = useRowHighlight('emailId');

  useEffect(() => {
    setTopbar('Advisor Support', '');
  }, [setTopbar]);

  function getStatus(emailId: string): 'New' | 'Processing' | 'Responded' | 'Follow-up' {
    const sess = sessions.find(s => s.kind === 'advisor-email' && s.advisorEmail?.id === emailId);
    if (!sess) return 'New';
    if (sess.advisorApproved) return 'Responded';
    return 'Processing';
  }

  const statuses   = ADVISOR_SUPPORT_EMAILS.map(e => getStatus(e.id));
  const newCount   = statuses.filter(s => s === 'New').length;
  const urgentCount = ADVISOR_SUPPORT_EMAILS.filter(e => e.priority === 'urgent').length;

  function handleClick(email: AdvisorSupportEmail) {
    navigate('/ace', { state: { advisorEmail: email } });
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-wash">
      {/* Header */}
      <div className="px-8 pt-6 pb-5 flex-shrink-0 bg-white border-b border-trim">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-ink">Advisor Support</h1>
            <p className="text-xs text-muted mt-0.5">Registration name change requests from advisors</p>
          </div>
          <div className="flex items-center gap-6">
            {[
              { label: 'Total',  val: ADVISOR_SUPPORT_EMAILS.length, cls: 'text-ink' },
              { label: 'New',    val: newCount,                       cls: 'text-orange-600' },
              { label: 'Urgent', val: urgentCount,                    cls: 'text-red-600' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <div className={`text-2xl font-bold leading-none ${s.cls}`}>{s.val}</div>
                <div className="text-xs text-muted mt-1">{s.label}</div>
              </div>
            ))}
            <button className="ml-2 px-3 py-1.5 text-xs font-medium text-copy border border-trim rounded-lg hover:bg-wash transition-colors">
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-8 py-5">
        <div className="bg-white rounded-xl border border-trim shadow-card overflow-hidden">
          {/* Column headers */}
          <div
            className="grid px-4 py-2.5 border-b border-trim bg-wash text-xs font-semibold text-muted uppercase tracking-wider"
            style={{ gridTemplateColumns: COL }}
          >
            <div>Job ID</div>
            <div>Advisor</div>
            <div>Account</div>
            <div>Subject</div>
            <div>Received</div>
            <div>Priority</div>
            <div>SLA</div>
            <div>Status</div>
          </div>

          {ADVISOR_SUPPORT_EMAILS.map((email, i) => {
            const status = getStatus(email.id);
            return (
              <div
                key={email.id}
                ref={rowRef(email.id)}
                className={`grid items-center px-4 py-3 cursor-pointer transition-colors hover:bg-tint ${highlightClass(email.id)} ${
                  i < ADVISOR_SUPPORT_EMAILS.length - 1 ? 'border-b border-trim' : ''
                }`}
                style={{ gridTemplateColumns: COL }}
                onClick={() => handleClick(email)}
              >
                <div className="text-xs font-mono text-muted">{JOB_ID[email.id]}</div>
                <div>
                  <div className="text-sm font-medium text-ink leading-tight">{email.from}</div>
                  <div className="text-xs text-muted truncate">{email.fromEmail}</div>
                </div>
                <div className="text-xs font-mono text-copy">{email.accountNumber}</div>
                <div className="text-sm text-copy truncate pr-4">{email.subject}</div>
                <div className="text-xs text-muted">{email.date}</div>
                <div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded capitalize ${PRIORITY_CLS[email.priority]}`}>
                    {email.priority}
                  </span>
                </div>
                <div className="text-xs text-muted">{email.sla}</div>
                <div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_CLS[status]}`}>
                    {status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
